import React, { useEffect, useRef, useState } from 'react';
import logo from '../../../images/logo/IE-logo-2.png'
import printOnePage from 'utils/printOnePage';
import { getAccountID, getWebhookID, postWP } from 'services/wp';
import fakeDelay from 'utils/fakeDelay';
import { sendToGHL } from 'services/ghl';
import validateRequiredFields from 'utils/validateRequiredFields';
import Loading from 'components/Loading';
import AlertError from 'components/AlertError';
import validateEmail from 'utils/validateEmail';
import { useNavigate } from 'react-router-dom';
import Modal from 'components/Modal';
import getLocalStorage from 'utils/getLocalStorage';
import getDeviceFingerprint from 'utils/getDeviceFingerPrint';
import TermsAndConditionContent from 'routes/terms_and_condition/content/TermsAndConditionContent';
import PrivacyPolicyContent from 'routes/privacy_policy/content/PrivacyPolicyContent';
import { validatePhone, formatPhoneNumber } from 'utils/validatePhone';
import { validateZip, formatZipCode } from 'utils/validateZip';
import { validateEIN, formatEIN } from 'utils/validateEIN';
import { withSubmissionTimeout, getSubmissionErrorMessage } from 'utils/submissionTimeout';

const TCPA_GROUP_CENSUS_DISCLAIMER =
  "I agree to the Terms & Conditions, Privacy Policy, and Communication Agreement. By checking this box and clicking 'Submit Group Census Form', I provide my express written consent for Insurance Enterprise USA to contact me regarding group health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. Consent is not a condition of purchase.";

const GroupEnrollmentForm = () => {
  const navigate = useNavigate();

  // VALIDATION && ERROR HANDLING
  const [showError, setShowError] = useState(false);
  const [validationErrorText, setValidationErrorText] = useState('Error! Some required fields are empty.');
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState('');
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // PAGE REF
  const groupEnrollmentRef = useRef(null);

  // STATES
  const styledInput = {
    border: '1px solid rgba(0,0,0,0.2)'
  };
  const styledNotValid = {
    border: '2px solid #dc2626',
    backgroundColor: 'rgba(254, 226, 226, 0.85)'
  };
  const reqBadge = "bg-red-600 text-white text-[8px] font-bold px-1 py-[0.5px] rounded shadow z-20 whitespace-nowrap pointer-events-none";

  const [phoneValidity, setPhoneValidity] = useState(true);
  const [einValidity, setEinValidity] = useState(true);
  const [zipValidity, setZipValidity] = useState(true);
  const [groupQuotePrimaryInfo, setGroupQuotePrimaryInfo] = useState({
    companyName: '',
    totalNumberOfEmployees: '',
    companyEIN: '',
    companyAddress: '',
    city: '',
    state: '',
    zipCode: '',
    pointOfContactName: '',
    POCPhoneNumber: '',
    POCEmail: '',
    isCurrentlyCoverage: '',
  });
  const [groupQuotePrimaryInfoValidity, setGroupQuotePrimaryInfoValidity] = useState({
    companyName: true,
    totalNumberOfEmployees: true,
    companyEIN: true,
    companyAddress: true,
    city: true,
    state: true,
    zipCode: true,
    pointOfContactName: true,
    POCPhoneNumber: true,
    POCEmail: true,
    isCurrentlyCoverage: true,
  });

  const [mostImportant, setMostImportant] = useState('');
  const [mostImportantOther, setMostImportantOther] = useState('');
  const [mostImportantRadio, setMostImportantRadio] = useState('');

  const [effectiveDateValidity, seteffectiveDateValidity] = useState(true);
  const [desiredEffectiveDate, setDesiredEffectiveDate] = useState({
    state: '',
    when: ''
  });

  const [healthInsuranceNeeds, setHealthInsuranceNeeds] = useState('');
  const [idealConditions, setIdealConditions] = useState('');

  const [groupQuoteParticipants, setGroupQuoteParticipants] = useState(Array(25).fill(
    {
      employeeName: '',
      gender: '',
      employeeDateOfBirth: '',
      tobacco: '',
      coverageType: '',
      spouseGender: '',
      employeeSpouseGender: '',
      employeeSpouseDateOfBirth: '',
      spouseTobacco: '',
      numberOfDependents: '',
      medicalConditions: '',
    }
  ));

  const [isTextAreaHidden, setIsTextAreaHidden] = useState(false);

  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [legalTab, setLegalTab] = useState('comm');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [tcpaError, setTcpaError] = useState(false);


  // ONCHANGE HANDLERS
  const handleGroupQuotePrimaryInfoChange = (e) => {
    setShowError(false);

    let { name, value } = e.target;

    if (name === 'POCPhoneNumber') {
      setPhoneValidity(true);
      value = formatPhoneNumber(value);
    } else if (name === 'zipCode') {
      setZipValidity(true);
      value = formatZipCode(value);
    } else if (name === 'companyEIN') {
      setEinValidity(true);
      value = formatEIN(value);
    } else if (name === 'isCurrentlyCoverage') {
      value = value.toUpperCase().replace(/[^YN]/g, '').slice(0, 1);
    }

    setGroupQuotePrimaryInfo(prevState => ({
      ...prevState,
      [name]: value
    }))

    // remove error to the current field
    setGroupQuotePrimaryInfoValidity(prevState => ({
      ...prevState,
      [name]: true
    }))

    // error when current required field is empty
    if (value === '' && name !== 'companyEIN' && name !== 'isCurrentlyCoverage') {
      setGroupQuotePrimaryInfoValidity(prevState => ({
        ...prevState,
        [name]: false
      }))
    }

    // remove error to the email field
    if (name === 'POCEmail') {
      setInvalidEmail(false);
    }
  }

  const handleOtherChoice = (e) => {
    setShowError(false);

    setMostImportantRadio('')

    const { value } = e.target;
    setMostImportantOther(value)
    setMostImportant(value);
  }

  const handleMostImportantChange = (e) => {
    setShowError(false);

    setMostImportantOther('');

    const { value } = e.target;
    setMostImportantRadio(value);
    setMostImportant(value);
  }

  const handleDesiredEffectiveDateChange = (e) => {
    setShowError(false);
    seteffectiveDateValidity(true);

    const { name, value } = e.target;
    if (name === 'isDesiredEffectiveDate') {
      if (value === 'Yes') {
        setDesiredEffectiveDate({
          state: 'Yes',
          when: ''
        })
      } else {
        setDesiredEffectiveDate({
          state: 'No',
          when: ''
        });
      }
    } else {
      setDesiredEffectiveDate(prevState => ({
        ...prevState,
        state: 'Yes',
        when: value
      }))
    }
  }

  const handleHealthInsuranceNeedsChange = (e) => {
    setShowError(false);

    setHealthInsuranceNeeds(e.target.value);
  }

  const handleIdealConditionsChange = (e) => {
    setShowError(false);

    setIdealConditions(e.target.value);
  }

  const handleGroupQuoteParticipantsChange = (e, index, fieldOverride) => {
    setShowError(false);

    const name = fieldOverride || e.target.name;
    let value = e.target.value;

    if (name === 'medicalConditions') {
      // Only P (Primary), S (Spouse), D (Dependent) — each once, e.g. "P, S, D"
      const letters = [...new Set(value.toUpperCase().replace(/[^PSD]/g, ''))];
      value = letters.join(', ');
    }

    setGroupQuoteParticipants((prevParticipants) => {
      const newParticipants = [...prevParticipants];
      newParticipants[index] = {
        ...newParticipants[index],
        [name]: value,
      };
      return newParticipants;
    });
  }



  // ACTIONS
  const [scale, setScale] = useState(100);


  const validate = async () => {
    const requiredFieldLabels = {
      companyName: 'Company Name',
      totalNumberOfEmployees: 'Total # of Employees',
      companyAddress: 'Company Address',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      pointOfContactName: 'Point of Contact Name',
      POCPhoneNumber: 'POC Phone Number',
      POCEmail: 'POC Email',
    };

    const missingList = [];
    const invalidList = [];
    const nextValidity = { ...groupQuotePrimaryInfoValidity };

    for (const [key, label] of Object.entries(requiredFieldLabels)) {
      const val = String(groupQuotePrimaryInfo[key] || '').trim();
      if (!val) {
        nextValidity[key] = false;
        missingList.push(label);
      } else {
        nextValidity[key] = true;
      }
    }

    // Optional EIN check
    const einValid = validateEIN(groupQuotePrimaryInfo.companyEIN);
    setEinValidity(einValid);
    if (!einValid) {
      invalidList.push('Company EIN (XX-XXXXXXX)');
    }

    // Zip Code format check
    const zipStr = String(groupQuotePrimaryInfo.zipCode || '').trim();
    if (zipStr && !validateZip(zipStr)) {
      setZipValidity(false);
      nextValidity.zipCode = false;
      invalidList.push('Zip Code (5 digits)');
    } else {
      setZipValidity(true);
    }

    // Phone format check
    const phoneStr = String(groupQuotePrimaryInfo.POCPhoneNumber || '').trim();
    if (phoneStr && !validatePhone(phoneStr)) {
      setPhoneValidity(false);
      nextValidity.POCPhoneNumber = false;
      invalidList.push('POC Phone Number (10 digits)');
    } else {
      setPhoneValidity(true);
    }

    // Email format check
    const emailStr = String(groupQuotePrimaryInfo.POCEmail || '').trim();
    if (emailStr && !validateEmail(emailStr)) {
      setInvalidEmail(true);
      nextValidity.POCEmail = false;
      invalidList.push('POC Email');
    } else {
      setInvalidEmail(false);
    }

    // Desired Effective Date check when "Yes" is selected
    if (desiredEffectiveDate.state === 'Yes' && !String(desiredEffectiveDate.when || '').trim()) {
      seteffectiveDateValidity(false);
      missingList.push('Desired Effective Date');
    } else {
      seteffectiveDateValidity(true);
    }

    setGroupQuotePrimaryInfoValidity(nextValidity);

    if (missingList.length > 0 || invalidList.length > 0) {
      const parts = [];
      if (missingList.length > 0) {
        const preview = missingList.slice(0, 4).join(', ');
        const extra = missingList.length > 4 ? ` (+${missingList.length - 4} more)` : '';
        parts.push(`Missing required fields: ${preview}${extra}.`);
      }
      if (invalidList.length > 0) {
        parts.push(`Invalid format: ${invalidList.join(', ')}.`);
      }
      setValidationErrorText(`Error! ${parts.join(' ')}`);
      setShowError(true);
      setTimeout(() => {
        const firstInvalid = document.querySelector('[data-invalid="true"]');
        if (firstInvalid && typeof firstInvalid.scrollIntoView === 'function') {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
      return;
    }

    setShowError(false);
    openDisclaimer();
  }

  const setupDocument = async () => {
    console.log();
    setIsTextAreaHidden(true);

    await fakeDelay(5000);

    console.log();
    const formData = new FormData();
    const printedDoc = await printOnePage(groupEnrollmentRef)

    formData.append('file', printedDoc, `GroupHealthForm-${groupQuotePrimaryInfo.companyName.replaceAll(' ', '')}.pdf`);
    formData.append('userEmail', groupQuotePrimaryInfo.POCEmail);

    return formData;
  }

  const submitFile = async () => {
    setScale(100);
    setLoading(true);
    setSubmissionFailed(false);
    const cleanPhone = groupQuotePrimaryInfo.POCPhoneNumber.replace(/[()\-\s]/g, '');

    const deviceData = getDeviceFingerprint();
    const json = {
      companyEIN: groupQuotePrimaryInfo.companyEIN,
      isCurrentlyCoverage: groupQuotePrimaryInfo.isCurrentlyCoverage,
      mostImportant: mostImportant,
      desiredEffectiveDate: desiredEffectiveDate.state === 'Yes' ? desiredEffectiveDate.when : "",
      healthInsuranceNeeds: healthInsuranceNeeds,
      idealConditions: idealConditions,

      // Required Fields
      companyName: groupQuotePrimaryInfo.companyName,
      totalNumberOfEmployees: parseInt(groupQuotePrimaryInfo.totalNumberOfEmployees),
      companyAddress: groupQuotePrimaryInfo.companyAddress,
      city: groupQuotePrimaryInfo.city,
      state: groupQuotePrimaryInfo.state,
      zipCode: groupQuotePrimaryInfo.zipCode,
      pointOfContactName: groupQuotePrimaryInfo.pointOfContactName,
      POCPhoneNumber: cleanPhone,
      POCEmail: groupQuotePrimaryInfo.POCEmail,

      // TCPA Compliance & Audit Logging Fields
      tcpaConsent: true,
      tcpaConsentCaptured: true,
      tcpaConsentTimestamp: new Date().toISOString(),
      exactDisclaimerText: TCPA_GROUP_CENSUS_DISCLAIMER,

      // User device info
      ...deviceData
    }

    try {
      await withSubmissionTimeout(async () => {
        const document = await setupDocument();
        const resFromDrive = await postWP("custom/v1/upload", document);

        if (resFromDrive && resFromDrive.success && resFromDrive.link) {
          json.link = resFromDrive.link;
          const accountID = await getAccountID();
          const webhookID = await getWebhookID({ formName: "group_enroll" })
          const isSent = await sendToGHL(json, accountID, webhookID);

          if (!isSent) {
            throw new Error("Failed to send Group Census Form to GHL.");
          }
        } else {
          console.error("Upload incomplete:", resFromDrive?.message);
          throw new Error(resFromDrive?.message || "Upload incomplete.");
        }
      });

      setIsDisclaimerOpen(false);
      setSuccess(true);
    } catch (error) {
      console.log(error);
      setSubmissionErrorMessage(getSubmissionErrorMessage(error));
      setSubmissionFailed(true);
      setIsDisclaimerOpen(false);
    } finally {
      setIsTextAreaHidden(false);
      setLoading(false);
    }
  }


  const redirectToHomePage = () => {
    document.body.style.overflow = "auto"
    navigate('/');
  }

  const openDisclaimer = (tab = 'comm') => {
    setLegalTab(tab);
    setTcpaError(false);
    setIsDisclaimerOpen(true);
  }

  const closeDislaimer = () => {
    if (loading) return;
    setIsDisclaimerOpen(false);
  }

  const handleDisclaimerSubmit = () => {
    if (!agreeToTerms) {
      setTcpaError(true);
      return;
    }
    submitFile();
  }

  useEffect(() => {

    loading ? document.body.style.overflow = "hidden" : document.body.style.overflow = "auto";

    if (success) document.body.style.overflow = "hidden";

  }, [loading, success]);

  const [scaledPDF, setScaledPDF] = useState(816);

  const zoomIn = () => {
    const nextScale = scale + 5;
    const scaled = scaledPDF * (nextScale / scale); // relative scaling
    const viewportWidth = window.innerWidth;

    if (scaled < viewportWidth) {
      setScale(nextScale);
      setScaledPDF(scaled);
    }
  };

  const zoomOut = () => {
    if (scale > 100) {
      const nextScale = scale - 5;
      const scaled = scaledPDF * (nextScale / scale); // relative scaling
      setScale(nextScale);
      setScaledPDF(scaled);
    }
  };


  const reset = () => {
    setScale(100);
    setScaledPDF(816)
  }

  useEffect(() => {
    const sticky = document.getElementById('stickyTargetGroup');
    const parent = sticky.parentElement;

    const handleScroll = () => {
      const parentRect = parent.getBoundingClientRect();
      const stickyHeight = sticky.offsetHeight;

      if (parentRect.bottom <= window.innerHeight + stickyHeight) {
        sticky.classList.remove('opacity-100');
        // sticky.classList.add('absolute');
        sticky.classList.add('opacity-0');
      } else {
        sticky.classList.remove('opacity-0');
        // sticky.classList.add('fixed');
        sticky.classList.add('opacity-100');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const savedGroupHealthData = getLocalStorage('groupHealthForm');
    if (savedGroupHealthData) {
      const groupHealthData = JSON.parse(savedGroupHealthData);
      setGroupQuotePrimaryInfo(prev => ({
        ...prev,
        pointOfContactName: groupHealthData.fullName,
        POCEmail: groupHealthData.email,
        state: groupHealthData.selectedState,
        POCPhoneNumber: formatPhoneNumber(groupHealthData.phoneNumber),
      }))

      setGroupQuotePrimaryInfoValidity(prev => ({
        ...prev,
        pointOfContactName: true,
        POCEmail: true,
        state: true,
        POCPhoneNumber: true
      }))
    }




  }, []);



  return (
    <>
      <div className={`relative overflow-auto border-y`}>
        <div style={{ transform: `scale(${scale / 100})` }} className='duration-300 transition-all origin-top-left md:origin-top'>
          <div className='py-16 px-4 flex flex-col items-start md:items-center'>

            <div ref={groupEnrollmentRef} className='w-[816px] min-h-[1104px] border border-gray-300 bg-white text-[9.2px] font-pdf p-[24px] shadow-xl relative'>
              <div className='border-[4px] w-full border-[#305496] relative'>

                {/* 1st row */}
                <div className='grid grid-cols-3'>
                  <div className='font-bold'>FOR OFFICE USE ONLY</div>
                  <div className='border-r border-l border-[#305496] font-[700] tracking-tighter py-7 text-center text-[17px]'>GROUP CENSUS FORM</div>
                  <div className='font-bold flex flex-col justify-center items-center text-blue-600'>
                    <div><img src={logo} alt='IE logo' style={{ height: '40px' }} /></div>
                    <div className='text-sm font-bold'>(888) 350-6605</div>
                    <div>www.insuranceenterpriseusa.com</div>
                  </div>
                </div>
              </div>

              {/* 2nd row */}
              <div className='grid grid-cols-1'>
                <div className='border-b-[3px] border-x-[4px] font-bold border-[#305496] bg-indigo-300 text-center'>PLEASE PRINT ELIGIBLY</div>
              </div>

              {/* 3rd row */}
              <div className='grid'>

                <div className='grid grid-cols-14 border-b min-h-[23px] border-[#305496]'>

                  <div className='border-r border-l-[4px] px-1 border-[#305496] col-span-5 flex items-center min-w-0'>

                    <div className='flex justify-between items-center w-full min-w-0'>

                      <div className=' whitespace-nowrap mr-1.5 flex items-center'>Company Name:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 flex-1 text-right'>
                        {!groupQuotePrimaryInfoValidity.companyName && !loading && (
                          <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>Required</div>
                        )}
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block text-right'>{groupQuotePrimaryInfo.companyName}</span>
                        ) : (
                          <input
                            type='text'
                            name='companyName'
                            data-invalid={!groupQuotePrimaryInfoValidity.companyName}
                            style={
                              groupQuotePrimaryInfoValidity.companyName ?
                                styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.companyName}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>

                  </div>

                  <div className='border-r border-[#305496] px-1 col-span-3 relative flex items-center min-w-0'>
                    <div className='flex justify-between items-center w-full min-w-0'>
                      <div className='whitespace-nowrap mr-1 flex items-center text-[8.5px]'>Total # of Employees:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 flex-1 text-right'>
                        {!groupQuotePrimaryInfoValidity.totalNumberOfEmployees && !loading && (
                          <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>Required</div>
                        )}
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block text-right'>{groupQuotePrimaryInfo.totalNumberOfEmployees}</span>
                        ) : (
                          <input
                            type='number'
                            name='totalNumberOfEmployees'
                            data-invalid={!groupQuotePrimaryInfoValidity.totalNumberOfEmployees}
                            style={
                              groupQuotePrimaryInfoValidity.totalNumberOfEmployees ?
                                styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.totalNumberOfEmployees}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border-r-[4px] border-[#305496] px-1 col-span-6 relative flex items-center min-w-0'>
                    <div className='flex justify-between items-center w-full min-w-0'>
                      <div className='whitespace-nowrap mr-1.5'>Company EIN:</div>
                      <div className='relative min-w-0 flex-1 text-right'>
                        <div className={`absolute ${!einValidity && !loading ? 'block' : 'hidden'} right-0 -top-3.5 ${reqBadge}`}>
                          Invalid 9-digit EIN (XX-XXXXXXX)
                        </div>
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block text-right'>{groupQuotePrimaryInfo.companyEIN}</span>
                        ) : (
                          <input
                            type='text'
                            inputMode='numeric'
                            maxLength={10}
                            name='companyEIN'
                            placeholder='XX-XXXXXXX'
                            data-invalid={!einValidity}
                            style={einValidity ? styledInput : styledNotValid}
                            value={groupQuotePrimaryInfo.companyEIN}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className='bg-transparent w-full h-[17px]'
                          />
                        )}
                      </div>
                    </div>
                  </div>



                </div>
              </div>

              {/* 4th row */}
              <div className='grid'>

                <div className='grid grid-cols-14 border-b border-[#305496] min-h-[23px]'>

                  <div className='border-r border-l-[4px] border-[#305496] px-1 col-span-5 relative flex items-center min-w-0'>
                    <div className='flex justify-between items-center w-full min-w-0'>
                      <div className='whitespace-nowrap mr-1.5 flex items-center'>Company Address:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 flex-1 text-right'>
                        {!groupQuotePrimaryInfoValidity.companyAddress && !loading && (
                          <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>Required</div>
                        )}
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block text-right'>{groupQuotePrimaryInfo.companyAddress}</span>
                        ) : (
                          <input
                            type='text'
                            name='companyAddress'
                            data-invalid={!groupQuotePrimaryInfoValidity.companyAddress}
                            style={
                              groupQuotePrimaryInfoValidity.companyAddress ?
                                styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.companyAddress}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] px-1 col-span-3 relative flex items-center min-w-0'>
                    <div className='flex justify-between items-center w-full min-w-0'>
                      <div className='whitespace-nowrap mr-1.5 flex items-center'>City:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 flex-1 text-right'>
                        {!groupQuotePrimaryInfoValidity.city && !loading && (
                          <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>Required</div>
                        )}
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block text-right'>{groupQuotePrimaryInfo.city}</span>
                        ) : (
                          <input
                            type='text'
                            name='city'
                            data-invalid={!groupQuotePrimaryInfoValidity.city}
                            style={
                              groupQuotePrimaryInfoValidity.city ?
                                styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.city}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] px-1 col-span-3 relative flex items-center min-w-0'>
                    <div className='flex justify-between items-center w-full min-w-0'>
                      <div className='whitespace-nowrap mr-1.5 flex items-center'>State:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 flex-1 text-right'>
                        {!groupQuotePrimaryInfoValidity.state && !loading && (
                          <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>Required</div>
                        )}
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block text-right'>{groupQuotePrimaryInfo.state}</span>
                        ) : (
                          <input
                            type='text'
                            name='state'
                            data-invalid={!groupQuotePrimaryInfoValidity.state}
                            style={
                              groupQuotePrimaryInfoValidity.state ?
                                styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.state}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border-r-[4px] border-[#305496] px-1 col-span-3 relative flex items-center min-w-0'>
                    <div className='flex justify-between items-center w-full min-w-0'>
                      <div className='whitespace-nowrap mr-1.5 flex items-center'>Zip Code:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 flex-1 text-right'>
                        {(!zipValidity || !groupQuotePrimaryInfoValidity.zipCode) && !loading && (
                          <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>
                            {!zipValidity ? 'Invalid 5-Digit ZIP' : 'Required'}
                          </div>
                        )}
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block text-right'>{groupQuotePrimaryInfo.zipCode}</span>
                        ) : (
                          <input
                            type='text'
                            inputMode='numeric'
                            maxLength={5}
                            name='zipCode'
                            placeholder="5-digit ZIP"
                            data-invalid={!groupQuotePrimaryInfoValidity.zipCode || !zipValidity}
                            style={
                              groupQuotePrimaryInfoValidity.zipCode && zipValidity ?
                                styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.zipCode}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>
                  </div>


                </div>
              </div>

              {/* 5th row */}
              <div className='grid'>

                <div className='grid grid-cols-14 border-b border-[#305496] min-h-[35px]'>

                  <div className='border-r border-l-[4px] border-[#305496] px-1 col-span-5 relative flex flex-col justify-center min-w-0'>
                    <div className='flex flex-col'>
                      <div className='h-3 whitespace-nowrap flex items-center'>Point of Contact Name:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 w-full mt-[2px]'>
                        {!groupQuotePrimaryInfoValidity.pointOfContactName && !loading && (
                          <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>Required</div>
                        )}
                        {loading ? (
                          <span className='break-words leading-tight text-[9px] font-normal inline-block'>{groupQuotePrimaryInfo.pointOfContactName}</span>
                        ) : (
                          <input
                            type='text'
                            name='pointOfContactName'
                            data-invalid={!groupQuotePrimaryInfoValidity.pointOfContactName}
                            style={
                              groupQuotePrimaryInfoValidity.pointOfContactName ?
                                styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.pointOfContactName}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] px-1 col-span-3 relative flex flex-col justify-center min-w-0'>
                    <div className='flex flex-col'>
                      <div className='h-3 whitespace-nowrap flex items-center'>POC Phone Number:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 w-full mt-[2px]'>
                        <div className={`absolute ${(!phoneValidity || !groupQuotePrimaryInfoValidity.POCPhoneNumber) && !loading ? 'block' : 'hidden'} -top-3.5 left-0 ${reqBadge}`}>
                          {!phoneValidity ? 'Invalid 10-digit phone' : 'Required'}
                        </div>
                        {loading ? (
                          <span className='whitespace-nowrap text-[9px] font-normal inline-block'>{groupQuotePrimaryInfo.POCPhoneNumber}</span>
                        ) : (
                          <input
                            required
                            type='tel'
                            name='POCPhoneNumber'
                            placeholder="(XXX) XXX-XXXX"
                            data-invalid={!groupQuotePrimaryInfoValidity.POCPhoneNumber || !phoneValidity}
                            style={
                              groupQuotePrimaryInfoValidity.POCPhoneNumber && phoneValidity
                                ? styledInput
                                : styledNotValid
                            }
                            value={groupQuotePrimaryInfo.POCPhoneNumber}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className="bg-transparent w-full h-[17px]"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] px-1 col-span-3 relative flex flex-col justify-center min-w-0'>
                    <div className='flex flex-col'>
                      <div className='h-3 whitespace-nowrap flex items-center'>POC Email:<span className={`text-error font-bold text-xs ml-0.5 ${isTextAreaHidden ? 'hidden' : ''}`}>*</span></div>
                      <div className='relative min-w-0 w-full mt-[2px]'>

                        <div className={`absolute ${(invalidEmail || !groupQuotePrimaryInfoValidity.POCEmail) && !loading ? 'block' : 'hidden'} -top-3.5 left-0 ${reqBadge}`}>
                          {invalidEmail ? 'Invalid Email' : 'Required'}
                        </div>
                        {loading ? (
                          <span className='break-all leading-tight text-[9px] font-normal inline-block'>{groupQuotePrimaryInfo.POCEmail}</span>
                        ) : (
                          <input
                            type='email'
                            name='POCEmail'
                            data-invalid={invalidEmail || !groupQuotePrimaryInfoValidity.POCEmail}
                            value={groupQuotePrimaryInfo.POCEmail}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            style={invalidEmail || !groupQuotePrimaryInfoValidity.POCEmail ? styledNotValid : styledInput}
                            className={`bg-transparent w-full h-[17px]`}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border-r-[4px] border-[#305496] px-1 col-span-3 relative flex flex-col justify-center min-w-0'>
                    <div className='flex flex-col'>
                      <div className='h-3 relative whitespace-nowrap'>Is there currently coverage?(Y/N):</div>
                      <div className='min-w-0 w-full mt-[2px]'>
                        {loading ? (
                          <span className='text-[9px] font-normal inline-block'>{groupQuotePrimaryInfo.isCurrentlyCoverage}</span>
                        ) : (
                          <input
                            type='text'
                            name='isCurrentlyCoverage'
                            maxLength={1}
                            placeholder='Y or N'
                            style={styledInput}
                            value={groupQuotePrimaryInfo.isCurrentlyCoverage}
                            onChange={handleGroupQuotePrimaryInfoChange}
                            className='bg-transparent w-full h-[17px]'
                          />
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* 6th row */}
              <div className='grid'>

                <div className='grid grid-cols-14 border-b border-[#305496]'>

                  <div className='border-r border-l-[4px] border-[#305496] px-1 col-span-5'>
                    <div>With providing insurance health insurance to your employees, what is most important to your business?</div>
                    <div>
                      <div className='grid grid-cols-2 gap-2 w-56 mt-1'>

                        <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                          name='mostImportant'
                          value='Premium Cost'
                          checked={mostImportantRadio === 'Premium Cost'}
                          onChange={handleMostImportantChange}
                          readOnly /> Premium Cost</div>

                        <div className=''>
                          <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                            name='mostImportant'
                            value='ACA Compliance'
                            checked={mostImportantRadio === 'ACA Compliance'}
                            onChange={handleMostImportantChange}
                            readOnly /> ACA Compliance</div>
                        </div>

                        <div className=''>
                          <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                            name='mostImportant'
                            value='Coverage/Benefits'
                            checked={mostImportantRadio === 'Coverage/Benefits'}
                            onChange={handleMostImportantChange}
                            readOnly /> Coverage/Benefits</div>
                        </div>

                        <div className='flex items-center'>
                          <label>Other</label>
                          {loading ? (
                            <span className='ml-1 text-[9px] font-normal break-words inline-block'>{mostImportantOther}</span>
                          ) : (
                            <input
                              type='text'
                              name='mostImportant'
                              style={styledInput}
                              value={mostImportantOther}
                              onChange={handleOtherChoice}
                              className='ml-1 w-[115px] h-[16px] mt-[2px]'
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] px-1 col-span-3 relative'>
                    <div>Do you have a desired effective date?</div>
                    <div>
                      <div className=' flex justify-start items-center my-1'>
                        <div className=''>
                          <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                            name='isDesiredEffectiveDate'
                            value='Yes'
                            checked={desiredEffectiveDate.state === 'Yes'}
                            onChange={handleDesiredEffectiveDateChange}
                            readOnly /> Yes, when?</div>
                        </div>
                        <div className='relative'>
                          {!effectiveDateValidity && !loading && (
                            <div className={`absolute -top-3.5 right-0 ${reqBadge}`}>Required</div>
                          )}
                          {loading ? (
                            <span className='ml-1 text-[9px] font-normal'>{desiredEffectiveDate.when}</span>
                          ) : (
                            <input
                              type='date'
                              name='desiredEffectiveDate'
                              data-invalid={!effectiveDateValidity}
                              min={new Date().toISOString().split('T')[0]}
                              style={
                                effectiveDateValidity ?
                                  styledInput
                                  : styledNotValid
                              }
                              value={desiredEffectiveDate.when}
                              onChange={handleDesiredEffectiveDateChange}
                              className='border-b border-black ml-1'
                            />
                          )}
                        </div>
                      </div>
                      <div className=''>
                        <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                          name='isDesiredEffectiveDate'
                          value='No'
                          checked={desiredEffectiveDate.state === 'No'}
                          onChange={handleDesiredEffectiveDateChange}
                          readOnly /> No</div>
                      </div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] px-1 col-span-3 relative'>
                    <div>Which best identifies your business currently with health insurance needs?</div>
                    <div>
                      <div className='my-1 space-y-1'>
                        <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                          name='healthInsuranceNeeds'
                          value='Shopping around'
                          checked={healthInsuranceNeeds === 'Shopping around'}
                          onChange={handleHealthInsuranceNeedsChange}
                          readOnly /> Shopping around</div>
                        <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                          name='healthInsuranceNeeds'
                          value='Unhappy with current provider'
                          checked={healthInsuranceNeeds === 'Unhappy with current provider'}
                          onChange={handleHealthInsuranceNeedsChange}
                          readOnly /> Unhappy with current provider</div>
                        <div className='flex items-center text-[9px] h-[10px]'><input type='radio' className='w-[9px] h-[9px] mr-[3px]'
                          name='healthInsuranceNeeds'
                          value='Ready to purchase'
                          checked={healthInsuranceNeeds === 'Ready to purchase'}
                          onChange={handleHealthInsuranceNeedsChange}
                          readOnly /> Ready to purchase</div>
                      </div>
                    </div>
                  </div>

                  <div className='border-r-[4px] border-[#305496] px-1 col-span-3 relative'>
                    <div>Please describe the ideal conditions you are looking for when purchasing health insurance:</div>
                    <div>

                      {isTextAreaHidden || loading ?
                        <div className='border min-h-[44px] p-1 flex items-start justify-start text-[8.5px] leading-tight break-words text-left'>
                          {idealConditions}
                        </div>
                        :
                        <textarea rows={3} value={idealConditions} onChange={handleIdealConditionsChange}
                          className='w-[90%] border text-start resize-none text-wrap' />
                      }
                    </div>
                  </div>


                </div>
              </div>

              {/* 7th row */}
              <div className='grid grid-cols-1'>
                <div className='border-y-[3px] border-x-[4px] font-bold border-[#305496] bg-indigo-300'>To generate an accurate group quote, please be as complete as possible with the information below.  Include only those who will be participating.</div>
              </div>

              {/* 8th row */}
              <div className='grid'>

                <div className='grid grid-cols-14 border-b border-[#305496]'>

                  <div className='border-r border-l-[4px] border-[#305496] text-center col-span-3 flex flex-col justify-center'>
                    <div>Employee Name</div>
                    <div>(Last, First, Middle)</div>
                  </div>

                  <div className='border-r border-[#305496] text-center relative col-span-3'>
                    <div>Primary Applicant Info</div>
                    <div className='grid grid-cols-4 border-t border-[#305496] divide-x divide-[#305496]'>
                      <div className='flex flex-col justify-center'>Gender</div>
                      <div className='col-span-2 flex flex-col justify-center'>Date of Birth</div>
                      <div>Tobacco Use?</div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] text-center col-span-2 relative flex flex-col justify-center'>
                    <div>Coverage Type</div>
                  </div>

                  <div className='border-r border-[#305496] text-center relative col-span-3'>
                    <div>Spouse Info</div>
                    <div className='grid grid-cols-4 border-t border-[#305496] divide-x divide-[#305496]'>
                      <div className='flex flex-col justify-center'>Gender</div>
                      <div className='col-span-2 flex flex-col justify-center'>Date of Birth</div>
                      <div>Tobacco Use?</div>
                    </div>
                  </div>

                  <div className='border-r border-[#305496] text-center relative flex flex-col justify-center'>
                    <div>Number of Dependents</div>
                  </div>

                  <div className='border-r-[4px] border-[#305496] text-center col-span-2 px-[1px]'>
                    <div>Medical Conditions</div>
                    <div>(P)Primary / (S)Spouse /</div>
                    <div>(D)Dependant</div>
                  </div>
                </div>
              </div>

              {/* 9th row and so on */}
              {
                groupQuoteParticipants.map((participant, index) => (
                  <div className='grid' key={index}>

                    <div className='grid grid-cols-14 border-b border-[#305496] min-h-[17px]'>

                      <div className='border-r border-l-[4px] border-[#305496] text-center col-span-3 flex divide-x divide-[#305496] min-w-0'>
                        <div className='w-3 flex items-center justify-center shrink-0'>{index + 1}</div>
                        <div className='flex items-center justify-center w-full min-w-0 px-1'>
                          {loading ? (
                            <span className='break-words leading-tight text-[8.5px] font-normal w-full text-center block'>{participant.employeeName}</span>
                          ) : (
                            <input type='text' name='employeeName'
                              style={styledInput}
                              value={participant.employeeName} onChange={(e) => handleGroupQuoteParticipantsChange(e, index)}
                              className='w-full h-[15px]' />
                          )}
                        </div>
                      </div>

                      <div className='border-r border-[#305496] grid grid-cols-4 divide-x divide-[#305496] text-center relative col-span-3'>
                        {/* Primary Applicant Info */}
                        <div className='flex flex-col justify-center items-center'>
                          <div className='my-1 space-y-1'>
                            <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                              <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                name={`gender_${index}`}
                                value='Male'
                                checked={participant.gender === 'Male'}
                                onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'gender')}
                              /> Male
                            </div>
                            <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                              <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                name={`gender_${index}`}
                                value='Female'
                                checked={participant.gender === 'Female'}
                                onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'gender')}
                              /> Female
                            </div>
                          </div>
                        </div>
                        <div className='col-span-2 flex justify-center items-center py-[2px] min-w-0'>
                          {loading ? (
                            <span className='text-[8.5px] font-normal'>{participant.employeeDateOfBirth}</span>
                          ) : (
                            <input type='date' name='employeeDateOfBirth'
                              className='w-[90%]'
                              style={styledInput}
                              value={participant.employeeDateOfBirth} onChange={(e) => handleGroupQuoteParticipantsChange(e, index)} />
                          )}
                        </div>
                        <div>
                          <div className='flex flex-col justify-center items-center'>
                            <div className='my-1 space-y-1'>
                              <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                                <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                  name={`tobacco_${index}`}
                                  value='Yes'
                                  checked={participant.tobacco === 'Yes'}
                                  onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'tobacco')}
                                /> Yes
                              </div>
                              <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                                <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                  name={`tobacco_${index}`}
                                  value='No'
                                  checked={participant.tobacco === 'No'}
                                  onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'tobacco')}
                                /> No
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='border-r border-[#305496] text-center col-span-2 relative flex flex-col justify-center items-center min-w-0 px-1'>
                        {loading ? (
                          <span className='text-[8.5px] font-normal break-words leading-tight'>{participant.coverageType}</span>
                        ) : (
                          <select
                            style={styledInput}
                            name='coverageType'
                            value={participant.coverageType}
                            onChange={(e) => handleGroupQuoteParticipantsChange(e, index)}
                            className='w-[95%] h-[15px]'
                          >
                            <option value="" defaultValue></option>
                            <option value="Individual">Individual</option>
                            <option value="Couple">Couple</option>
                            <option value="Individual plus child">Individual plus child</option>
                            <option value="Family">Family</option>
                          </select>
                        )}
                      </div>

                      <div className='border-r border-[#305496] grid grid-cols-4 divide-x divide-[#305496] text-center relative col-span-3'>
                        {/* Spouse Info */}
                        <div className='flex flex-col justify-center'>
                          <div className='my-1 space-y-1'>
                            <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                              <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                name={`spouseGender_${index}`}
                                value='Male'
                                checked={participant.spouseGender === 'Male'}
                                onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'spouseGender')}
                              /> Male
                            </div>
                            <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                              <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                name={`spouseGender_${index}`}
                                value='Female'
                                checked={participant.spouseGender === 'Female'}
                                onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'spouseGender')}
                              /> Female
                            </div>
                          </div>
                        </div>
                        <div className='col-span-2 flex justify-center items-center py-[2px] min-w-0'>
                          {loading ? (
                            <span className='text-[8.5px] font-normal'>{participant.employeeSpouseDateOfBirth}</span>
                          ) : (
                            <input type='date' name='employeeSpouseDateOfBirth'
                              className='w-[90%]'
                              style={styledInput}
                              value={participant.employeeSpouseDateOfBirth} onChange={(e) => handleGroupQuoteParticipantsChange(e, index)} />
                          )}
                        </div>
                        <div>
                          <div className='flex flex-col justify-center items-center'>
                            <div className='my-1 space-y-1'>
                              <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                                <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                  name={`spouseTobacco_${index}`}
                                  value='Yes'
                                  checked={participant.spouseTobacco === 'Yes'}
                                  onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'spouseTobacco')}
                                /> Yes
                              </div>
                              <div className='flex items-center text-[8px] h-[7px] ml-[1px]'>
                                <input type='radio' className='w-[7px] h-[7px] mr-[2px]'
                                  name={`spouseTobacco_${index}`}
                                  value='No'
                                  checked={participant.spouseTobacco === 'No'}
                                  onChange={(e) => handleGroupQuoteParticipantsChange(e, index, 'spouseTobacco')}
                                /> No
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='border-r border-[#305496] text-center relative flex items-center justify-center min-w-0'>
                        {loading ? (
                          <span className='text-[8.5px] font-normal'>{participant.numberOfDependents}</span>
                        ) : (
                          <input type='number' name='numberOfDependents'
                            style={styledInput}
                            value={participant.numberOfDependents} onChange={(e) => handleGroupQuoteParticipantsChange(e, index)} className='w-[90%] h-[15px]' />
                        )}
                      </div>

                      <div className='border-r-[4px] border-[#305496] text-center col-span-2 flex items-center justify-center min-w-0 px-1'>
                        {loading ? (
                          <span className='break-words leading-tight text-[8px] font-normal w-full text-center block'>{participant.medicalConditions}</span>
                        ) : (
                          <input type='text' name='medicalConditions'
                            maxLength={7}
                            placeholder='P, S, D'
                            style={styledInput}
                            value={participant.medicalConditions} onChange={(e) => handleGroupQuoteParticipantsChange(e, index)} className='w-[90%] h-[15px] text-center' />
                        )}
                      </div>
                    </div>
                  </div>
                ))
              }

              <div className='border-t-[3px] border-[#305496]'></div>

              {/* Footer */}
              <div className='w-full absolute bottom-8 left-0 flex justify-between text-[9px] px-6'>
                <div>Group Census Form</div>
                <div className='text-error'><span className='text-sm'>*</span> Required Field</div>
              </div>
            </div>

          </div >
        </div>

        <div id='stickyTargetGroup' className='fixed hidden bottom-4 left-1/2 -translate-x-1/2 my-4 md:flex justify-center space-x-2 transition-all ease-in-out duration-300'>
          <button className='btn btn-primary' onClick={zoomIn}>
            <svg className="w-6 h-6 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M10 7v6m-3-3h6m4 0a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
            </svg>
          </button>
          <button className='btn btn-primary' onClick={zoomOut}>
            <svg className="w-6 h-6 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M7 10h6m4 0a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
            </svg>

          </button>
          <button className='btn btn-primary text-white' onClick={reset}>Reset</button>
        </div>
      </div>

      {/* Unified 3-Tab Legal / TCPA Modal */}
      <Modal
        open={isDisclaimerOpen}
        handleClose={closeDislaimer}
        closeButton={false}
        disableBackgroundClose={true}
        maxWidth="sm:max-w-lg"
      >
        <div className="p-4 sm:p-5 space-y-3 text-left w-full mt-[-24px] sm:mt-[-30px]">
          {/* Header */}
          <div className="border-b border-base-200 pb-2">
            <h3 className="text-sm sm:text-base font-bold text-base-content">
              Agreements &amp; Disclosures
            </h3>
          </div>

          {/* 3 Tabs Bar */}
          <div className="flex border-b border-base-200 gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setLegalTab('comm')}
              className={`py-1.5 px-2.5 sm:px-3 text-xs font-bold transition-all rounded-t-lg border-b-2 ${
                legalTab === 'comm'
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:bg-base-200/50'
              }`}
            >
              Communication Agreement
            </button>
            <button
              type="button"
              onClick={() => setLegalTab('terms')}
              className={`py-1.5 px-2.5 sm:px-3 text-xs font-bold transition-all rounded-t-lg border-b-2 ${
                legalTab === 'terms'
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:bg-base-200/50'
              }`}
            >
              Terms &amp; Conditions
            </button>
            <button
              type="button"
              onClick={() => setLegalTab('privacy')}
              className={`py-1.5 px-2.5 sm:px-3 text-xs font-bold transition-all rounded-t-lg border-b-2 ${
                legalTab === 'privacy'
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:bg-base-200/50'
              }`}
            >
              Privacy Policy
            </button>
          </div>

          {/* Scrollable Tab Content Container */}
          <div className="modal-legal-content max-h-[200px] overflow-y-auto pr-2 text-[10px] text-base-content/85 leading-snug bg-base-200/30 p-2.5 sm:p-3 rounded-xl border border-base-200">
            {legalTab === 'terms' && (
              <div className="space-y-3">
                <TermsAndConditionContent />
              </div>
            )}

            {legalTab === 'privacy' && (
              <div className="space-y-3">
                <PrivacyPolicyContent />
              </div>
            )}

            {legalTab === 'comm' && (
              <div className="space-y-2.5">
                <div className="border-b border-base-200 pb-1">
                  <h4 className="text-xs font-bold text-base-content">
                    Electronic Communications Agreement &amp; TCPA Consent
                  </h4>
                  <p className="text-[9px] text-base-content/60">
                    Effective Date: August 4, 2026 • insuranceenterpriseusa.com
                  </p>
                </div>

                <p className="text-[10px] leading-relaxed">
                  By submitting your contact information (including your phone number and email address) through any online quote form, health plan calculator, enrollment form, or inquiry portal on this Site, you provide your express written consent and agreement to the following terms:
                </p>

                <div className="space-y-2 text-[9.5px] sm:text-[10px] leading-snug">
                  <div>
                    <h5 className="font-bold text-base-content text-[10px] sm:text-[10.5px]">1. Express Written Consent</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You authorize Insurance Enterprise USA, its licensed agents, affiliates, and representatives to contact you regarding health insurance quotes, plan options, coverage details, and related insurance services via telephone calls, SMS/MMS text messages, and email at the phone number and email address provided.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content text-[10px] sm:text-[10.5px]">2. Automated Technology</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You understand and agree that communications may be transmitted using automated telephone dialing systems (ATDS), artificial or prerecorded voice messages, automated SMS systems, and computer-generated emails.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content text-[10px] sm:text-[10.5px]">3. Consent Not Required for Purchase</h5>
                    <p className="text-base-content/80 mt-0.5">
                      Your consent to receive promotional and informational communications is completely voluntary and is <strong>not a condition of purchasing</strong> any goods, services, or insurance policies from Insurance Enterprise USA.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content text-[10px] sm:text-[10.5px]">4. Message Frequency &amp; Carrier Rates</h5>
                    <p className="text-base-content/80 mt-0.5">
                      Message frequency varies depending on your inquiries and active requests. Standard message and data rates charged by your mobile wireless carrier may apply to all SMS/MMS messages.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content text-[10px] sm:text-[10.5px]">5. Opt-Out &amp; Revocation of Consent</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You may revoke your consent or opt out at any time. To stop receiving text messages, reply <strong>STOP</strong> to any SMS received. To request help, reply <strong>HELP</strong>. To unsubscribe from emails, click the unsubscribe link at the bottom of any email. You may also contact our compliance department at <span className="font-bold text-primary">888-350-6605</span> or via email at <span className="font-bold text-primary">enroll@insuranceenterpriseusa.com</span>.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content text-[10px] sm:text-[10.5px]">6. E-SIGN Disclosure</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You consent to receive all notices, policy documents, disclosures, and communications electronically pursuant to the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mandatory TCPA Checkbox (Distinct Grayish Container) */}
          <div className="bg-slate-100 dark:bg-base-200/60 p-2.5 rounded-xl border border-slate-200 dark:border-base-300">
            <label className="label cursor-pointer flex items-start space-x-2 p-0">
              <input
                type="checkbox"
                disabled={loading}
                name="tcpaConsent"
                checked={agreeToTerms}
                onChange={(e) => {
                  setAgreeToTerms(e.target.checked);
                  if (tcpaError) setTcpaError(false);
                }}
                className={`checkbox checkbox-primary checkbox-xs rounded mt-0.5 shrink-0 ${
                  tcpaError ? 'checkbox-error' : ''
                }`}
              />
              <span className="label-text text-[9.5px] sm:text-[10px] leading-snug text-slate-500 dark:text-base-content/65">
                I agree to the{' '}
                <button
                  type="button"
                  disabled={loading}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLegalTab('terms');
                  }}
                  className="underline font-bold text-slate-700 dark:text-base-content/85 hover:text-primary cursor-pointer inline text-left bg-transparent border-0 p-0 text-[9.5px] sm:text-[10px]"
                >
                  Terms &amp; Conditions
                </button>
                ,{' '}
                <button
                  type="button"
                  disabled={loading}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLegalTab('privacy');
                  }}
                  className="underline font-bold text-slate-700 dark:text-base-content/85 hover:text-primary cursor-pointer inline text-left bg-transparent border-0 p-0 text-[9.5px] sm:text-[10px]"
                >
                  Privacy Policy
                </button>
                , and{' '}
                <button
                  type="button"
                  disabled={loading}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLegalTab('comm');
                  }}
                  className="underline font-bold text-slate-700 dark:text-base-content/85 hover:text-primary cursor-pointer inline text-left bg-transparent border-0 p-0 text-[9.5px] sm:text-[10px]"
                >
                  Communication Agreement
                </button>
                . By checking this box and clicking "Submit Group Census Form", I provide my express written consent for Insurance Enterprise USA to contact me regarding group health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. <strong className="text-slate-700 dark:text-base-content/90 font-semibold">Consent is not a condition of purchase.</strong>
              </span>
            </label>
            {tcpaError && (
              <span className="text-[10px] text-error mt-1.5 block font-semibold">
                Please check the agreement box to proceed.
              </span>
            )}
          </div>

          {/* Modal Footer Actions */}
          <div className="flex items-center justify-end space-x-2 pt-2 border-t border-base-200">
            <button
              type="button"
              disabled={loading}
              onClick={closeDislaimer}
              className={`btn btn-ghost btn-sm text-xs h-9 min-h-[36px] ${
                loading ? 'btn-disabled opacity-30 cursor-not-allowed pointer-events-none' : ''
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!agreeToTerms || loading}
              onClick={handleDisclaimerSubmit}
              className={`btn btn-sm px-5 text-xs font-black uppercase tracking-wider h-9 min-h-[36px] transition-all ${
                agreeToTerms && !loading
                  ? 'btn-secondary text-white shadow-md hover:scale-[1.01] active:scale-[0.99] cursor-pointer'
                  : loading
                  ? 'btn-secondary text-white opacity-85 cursor-wait'
                  : 'btn-disabled opacity-40 bg-base-300 text-base-content/40 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <span className="inline-flex items-center space-x-2">
                  <span className="loading loading-spinner loading-xs"></span>
                  <span>Submitting...</span>
                </span>
              ) : (
                'SUBMIT GROUP CENSUS FORM'
              )}
            </button>
          </div>
        </div>
      </Modal>



      {/* ACTION CONDITIONS */}

      <Loading Loading open={loading} >
        <div className='flex flex-col items-center space-y-4 text-center'>
          <div className='text-white text-4xl'>Sending your Group Census Form...</div>
          <span className="loading loading-spinner loading-lg text-white"></span>
        </div>
      </Loading >

      <AlertError showError={showError} onClose={() => setShowError(false)}>{validationErrorText}</AlertError>

      <AlertError showError={submissionFailed} onClose={() => setSubmissionFailed(false)}>
        {submissionErrorMessage || 'Something went wrong. Please try again later.'}
      </AlertError>

      {/* SUCCESS MESSAGE */}
      <Modal open={success} closeButton={false}>
        <div className='card'>
          <div className='card-body p-4'>
            <div className='card-title text-xl'>Group Census Form Received</div>
            <p className='text-sm'>
              An email confirmation has been sent to acknowledge your submission.
              If you do not see this email, please check your SPAM folder.
              One of our group specialists will contact you shortly with a group health proposal for your company.
              We look forward to working with you and thank you for trusting Insurance Enterprise USA for your group health insurance needs.
            </p>
          </div>
          <div className='flex justify-end card-actions'>
            <button className='btn btn-secondary text-white px-7' onClick={redirectToHomePage}>Close</button>
          </div>
        </div>
      </Modal>

      <div className='flex justify-center'>
        <div className='my-10 w-[816px] flex flex-col justify-center space-y-4 mx-4 md:space-y-0 md:flex-row md:justify-between'>
          <button onClick={() => navigate(-1)} disabled={loading} className='btn btn-lg btn-outline px-10 text-lg'>Back</button>
          <button onClick={validate} disabled={loading} className='btn btn-lg btn-secondary px-10 text-white text-lg'>Submit</button>
        </div>
      </div>
    </>

  );
};

export default GroupEnrollmentForm;
